import { Fragment, useMemo, useRef, useState, type ReactElement } from 'react';
import { Button, ColumnManager, Table, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui';
import { TextLink } from '@/components/ui/TextLink';
import { cx } from '@/lib/cx';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import type { VisaTutorClientRow, VisaTutorMetrics, VisaTutorResponse } from '@/lib/types';
import {
  PiCardField,
  PiCardList,
  PiEmpty,
  PiExpandButton,
  PiLoading,
  PiSection,
  PiSortHeader,
  piPct,
} from '../pi-kit';
import { GroupHeaderRow, GroupHeading } from '../events/events-ui';
import { formatCount } from '../use-fab-data';
import {
  clientKey,
  clientSubtitle,
  formatRating,
  formatScore,
  matchesClientSearch,
  splitClientGroups,
} from './visa-tutor-model';

/**
 * AI Visa Tutor per-client table: one row per school or university that holds
 * licences, split Institutions / Partners by the EMERGE company's list group.
 *
 * - The client name is SEED's; when the row resolved to an EMERGE company, the
 *   company is the `<Link>` under it.
 * - Search (`search`) filters client-side by client, university or company.
 * - Sort is client-side, applied inside each group, and remembered; default
 *   licences, descending. A missing value sorts last either way; ties fall back
 *   to the name.
 * - Totals row: the server's `overall` when nothing is searched; while a search
 *   narrows the list, the sum of every matching row's counts (including rows
 *   still behind "Show more"), with the two
 *   averages left as "—" (an average of averages would be a figure nobody
 *   measured).
 * - Secondary figures (score breakdown, outcomes, NPS) expand inline per row.
 * - Columns via `ColumnManager` (scope `product-insights.visa-tutor.clients`);
 *   Client is code-pinned left. Rows past 50 sit behind "Show more".
 */

type SortColumn =
  | 'client'
  | 'licencesTotal'
  | 'licencesAllocated'
  | 'licencesActivated'
  | 'studentsPractising'
  | 'sessions'
  | 'avgOverallScore'
  | 'approved'
  | 'rejected'
  | 'avgRating';
type FigureColumn = Exclude<SortColumn, 'client'>;
type SortDir = 'asc' | 'desc';
interface SortState {
  column: SortColumn;
  dir: SortDir;
}

const SORT_COLUMNS: readonly SortColumn[] = [
  'client',
  'licencesTotal',
  'licencesAllocated',
  'licencesActivated',
  'studentsPractising',
  'sessions',
  'avgOverallScore',
  'approved',
  'rejected',
  'avgRating',
];
const DEFAULT_SORT: SortState = { column: 'licencesTotal', dir: 'desc' };
const LAYOUT_SCOPE = 'product-insights.visa-tutor.clients';
const LOCKED_KEYS: readonly string[] = ['client'];
const PINNED_LEFT = { pinnedLeftKeys: LOCKED_KEYS };
const PAGE_SIZE = 50;

const LABELS: Record<SortColumn, string> = {
  client: 'Client',
  licencesTotal: 'Licences',
  licencesAllocated: 'Allocated',
  licencesActivated: 'Activated',
  studentsPractising: 'Practising',
  sessions: 'Sessions',
  avgOverallScore: 'Avg score',
  approved: 'Approved',
  rejected: 'Rejected',
  avgRating: 'Rating',
};

function isSortColumn(key: string): key is SortColumn {
  return SORT_COLUMNS.some((c) => c === key);
}

function parseSort(raw: unknown): SortState | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const { column, dir } = raw as { column?: unknown; dir?: unknown };
  if (dir !== 'asc' && dir !== 'desc') return undefined;
  const match = SORT_COLUMNS.find((c) => c === column);
  return match === undefined ? undefined : { column: match, dir };
}

type Figures = Record<FigureColumn, number | null>;

function figuresOf(row: VisaTutorMetrics): Figures {
  return {
    licencesTotal: row.licencesTotal,
    licencesAllocated: row.licencesAllocated,
    licencesActivated: row.licencesActivated,
    studentsPractising: row.studentsPractising,
    sessions: row.sessions,
    avgOverallScore: row.avgOverallScore,
    approved: row.approved,
    rejected: row.rejected,
    avgRating: row.avgRating,
  };
}

function formatFigure(column: FigureColumn, value: number | null): string {
  if (column === 'avgOverallScore') return formatScore(value);
  if (column === 'avgRating') return formatRating(value);
  return formatCount(value);
}

/** The secondary figures shown when a row expands. */
function detailPairs(row: VisaTutorMetrics): { label: string; value: string }[] {
  return [
    { label: 'Unallocated', value: formatCount(row.licencesUnallocated) },
    { label: 'Sessions completed', value: formatCount(row.sessionsCompleted) },
    { label: 'Communication score', value: formatScore(row.avgCommunicationScore) },
    { label: 'Body language score', value: formatScore(row.avgBodyLanguageScore) },
    { label: 'Visa score', value: formatScore(row.avgVisaScore) },
    { label: 'First score', value: formatScore(row.avgFirstScore) },
    { label: 'Latest score', value: formatScore(row.avgLatestScore) },
    { label: 'Best score', value: formatScore(row.avgBestScore) },
    { label: 'Outcomes reported', value: formatCount(row.outcomesReported) },
    { label: 'Processing', value: formatCount(row.processing) },
    { label: 'Interview pending', value: formatCount(row.interviewPending) },
    { label: 'Approval rate', value: piPct(row.approvalRate) },
    { label: 'Testimonials', value: formatCount(row.testimonials) },
    { label: 'NPS', value: row.nps === null ? '—' : String(Math.round(row.nps)) },
  ];
}

function DetailGrid({
  row,
  compact = false,
  className,
}: {
  row: VisaTutorMetrics;
  compact?: boolean;
  className?: string;
}) {
  return (
    <dl
      className={cx(
        'grid gap-x-6 gap-y-1.5 text-sm',
        compact ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-3 xl:grid-cols-5',
        className,
      )}
    >
      {detailPairs(row).map(({ label, value }) => (
        <div key={label} className="flex min-w-0 items-baseline justify-between gap-3">
          <dt className="truncate text-fg-muted">{label}</dt>
          <dd className="whitespace-nowrap tabular-nums">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ClientCell({ row }: { row: VisaTutorClientRow }) {
  const subtitle = clientSubtitle(row);
  return (
    <div className="min-w-0">
      <div className="truncate font-medium text-fg">{row.name}</div>
      {subtitle !== null && <div className="truncate text-xs text-fg-muted">{subtitle}</div>}
      {row.company != null && (
        <div className="min-w-0 text-xs">
          <TextLink to={`/companies/${row.company.id}`} className="truncate">
            {row.company.name}
          </TextLink>
        </div>
      )}
    </div>
  );
}

function CardFigures({ figures }: { figures: Figures }) {
  return (
    <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
      {SORT_COLUMNS.filter((c): c is FigureColumn => c !== 'client').map((column) => (
        <PiCardField
          key={column}
          label={LABELS[column]}
          value={<span className="tabular-nums">{formatFigure(column, figures[column])}</span>}
        />
      ))}
    </div>
  );
}

export function VisaTutorClientsTable({
  data,
  loading,
  search,
}: {
  data: VisaTutorResponse | null;
  loading: boolean;
  search: string;
}): ReactElement {
  const [sort, setSort] = useRememberedState<SortState>(
    'visa-tutor-clients-sort',
    DEFAULT_SORT,
    parseSort,
  );
  const needle = search.trim().toLowerCase();
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const [limitState, setLimitState] = useState({ needle, limit: PAGE_SIZE });
  const limit = limitState.needle === needle ? limitState.limit : PAGE_SIZE;

  const layout = useColumnLayout(LAYOUT_SCOPE, SORT_COLUMNS, LOCKED_KEYS, [], PINNED_LEFT);
  const { visibleKeys, frozenSideOf } = layout;
  const headRowRef = useRef<HTMLTableRowElement>(null);
  const frozenOffsets = useFrozenOffsets(headRowRef, visibleKeys, frozenSideOf);
  const frozenPropsOf = (key: string) => {
    const side = frozenSideOf(key);
    return side === undefined ? {} : { frozen: side, frozenOffset: frozenOffsets[key] ?? 0 };
  };
  const columns = visibleKeys.filter(isSortColumn);
  const columnCount = columns.length;

  const keyed = useMemo(
    () =>
      (data?.data.byClient ?? []).map((row, index) => ({ row, key: clientKey(row, index) })),
    [data],
  );
  const keyOf = useMemo(() => new Map(keyed.map(({ row, key }) => [row, key])), [keyed]);

  const groups = useMemo(() => {
    const visible = keyed.map(({ row }) => row).filter((row) => matchesClientSearch(row, needle));
    const factor = sort.dir === 'asc' ? 1 : -1;
    const byName = (a: VisaTutorClientRow, b: VisaTutorClientRow) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    const sorted = visible.slice().sort((a, b) => {
      if (sort.column === 'client') return byName(a, b) * factor;
      const left = a[sort.column];
      const right = b[sort.column];
      if (left === null && right === null) return byName(a, b);
      if (left === null) return 1;
      if (right === null) return -1;
      return left !== right ? (left - right) * factor : byName(a, b);
    });
    return splitClientGroups(sorted);
  }, [keyed, needle, sort.column, sort.dir]);

  const visibleCount = groups.reduce((sum, group) => sum + group.rows.length, 0);

  /** The groups cut to the first `limit` rows overall, empty groups dropped. */
  const shownGroups = useMemo(() => {
    let remaining = limit;
    const out: { label: string; total: number; rows: VisaTutorClientRow[] }[] = [];
    for (const group of groups) {
      if (remaining <= 0) break;
      const rows = group.rows.slice(0, remaining);
      remaining -= rows.length;
      out.push({ label: group.label, total: group.rows.length, rows });
    }
    return out;
  }, [groups, limit]);
  const hiddenCount = Math.max(0, visibleCount - limit);

  const totals = useMemo<Figures | null>(() => {
    if (data === null) return null;
    if (needle === '') return figuresOf(data.data.overall);
    const sum: Figures = {
      licencesTotal: 0,
      licencesAllocated: 0,
      licencesActivated: 0,
      studentsPractising: 0,
      sessions: 0,
      avgOverallScore: null,
      approved: 0,
      rejected: 0,
      avgRating: null,
    };
    for (const group of groups) {
      for (const row of group.rows) {
        sum.licencesTotal = (sum.licencesTotal ?? 0) + row.licencesTotal;
        sum.licencesAllocated = (sum.licencesAllocated ?? 0) + row.licencesAllocated;
        sum.licencesActivated = (sum.licencesActivated ?? 0) + row.licencesActivated;
        sum.studentsPractising = (sum.studentsPractising ?? 0) + row.studentsPractising;
        sum.sessions = (sum.sessions ?? 0) + row.sessions;
        sum.approved = (sum.approved ?? 0) + row.approved;
        sum.rejected = (sum.rejected ?? 0) + row.rejected;
      }
    }
    return sum;
  }, [data, needle, groups]);

  const onHeaderClick = (column: SortColumn) => {
    setSort(
      sort.column === column
        ? { column, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
        : { column, dir: column === 'client' ? 'asc' : 'desc' },
    );
  };
  const ariaSortOf = (column: SortColumn): 'ascending' | 'descending' | undefined =>
    sort.column !== column ? undefined : sort.dir === 'asc' ? 'ascending' : 'descending';

  const toggleExpanded = (key: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const manager = (
    <ColumnManager
      layout={layout}
      labelOf={(key) => (isSortColumn(key) ? LABELS[key] : key)}
      lockedKeys={LOCKED_KEYS}
      lockReasonOf={() => 'The client column identifies each row.'}
    />
  );

  const section = (body: ReactElement, count: number | null) => (
    <PiSection title="Clients" count={count} actions={manager} flush>
      {body}
    </PiSection>
  );

  if (data === null) {
    return section(<PiLoading variant="table" rows={8} />, null);
  }
  if (data.data.byClient.length === 0 || totals === null) {
    return section(
      <PiEmpty
        title="No AI Visa Tutor clients for this cycle."
        description="Choose another cycle to see its clients."
      />,
      0,
    );
  }
  if (visibleCount === 0) {
    return section(
      <PiEmpty
        title="No clients match this search."
        description="Clear the search to see every client."
      />,
      0,
    );
  }

  const figureCell = (column: FigureColumn, figures: Figures, strong: boolean) => {
    const emphasised = strong || column === 'approved';
    const muted =
      !strong && (column === 'avgOverallScore' || column === 'rejected' || column === 'avgRating');
    return (
      <TD
        key={column}
        numeric
        {...frozenPropsOf(column)}
        className={cx(
          'whitespace-nowrap tabular-nums',
          emphasised && 'font-medium',
          muted && 'text-fg-muted',
        )}
      >
        {formatFigure(column, figures[column])}
      </TD>
    );
  };

  const totalLabel = needle === '' ? 'Total' : 'Total of matching rows';
  const showMore =
    hiddenCount > 0 ? (
      <div className="flex justify-center border-t border-hairline p-3">
        <Button
          variant="secondary"
          className="max-sm:h-11"
          onClick={() => setLimitState({ needle, limit: limit + PAGE_SIZE })}
        >
          Show {formatCount(Math.min(PAGE_SIZE, hiddenCount))} more of {formatCount(hiddenCount)}
        </Button>
      </div>
    ) : null;

  return section(
    <div className={cx(loading && 'opacity-70')} aria-busy={loading || undefined}>
      {/* Desktop */}
      <div className="max-md:hidden">
        <TableWrap frozenHeader>
          <Table>
            <THead>
              <TR ref={headRowRef}>
                {columns.map((column) => (
                  <TH
                    key={column}
                    numeric={column !== 'client'}
                    aria-sort={ariaSortOf(column)}
                    {...frozenPropsOf(column)}
                    className={column === 'client' ? 'min-w-72' : 'min-w-20 whitespace-nowrap'}
                  >
                    <PiSortHeader
                      label={LABELS[column]}
                      active={sort.column === column}
                      direction={sort.column === column ? sort.dir : column === 'client' ? 'asc' : 'desc'}
                      onClick={() => onHeaderClick(column)}
                      align={column === 'client' ? 'left' : 'right'}
                    />
                  </TH>
                ))}
              </TR>
            </THead>
            <TBody>
              {shownGroups.map((group) => (
                <Fragment key={group.label}>
                  <GroupHeaderRow label={group.label} count={group.total} colSpan={columnCount} />
                  {group.rows.map((row) => {
                    const key = keyOf.get(row) ?? row.name;
                    const isOpen = expanded.has(key);
                    return (
                      <Fragment key={key}>
                        <TR>
                          {columns.map((column) =>
                            column === 'client' ? (
                              <TD key={column} className="max-w-96" {...frozenPropsOf(column)}>
                                <div className="flex min-w-0 items-start gap-1.5">
                                  <PiExpandButton
                                    expanded={isOpen}
                                    onClick={() => toggleExpanded(key)}
                                    label={`${isOpen ? 'Hide' : 'Show'} details for ${row.name}`}
                                  />
                                  <ClientCell row={row} />
                                </div>
                              </TD>
                            ) : (
                              figureCell(column, figuresOf(row), false)
                            ),
                          )}
                        </TR>
                        {isOpen && (
                          <TR>
                            <TD colSpan={columnCount} className="bg-surface-sunken">
                              <DetailGrid row={row} className="py-1" />
                            </TD>
                          </TR>
                        )}
                      </Fragment>
                    );
                  })}
                </Fragment>
              ))}
              <TR className="border-t border-hairline-strong">
                {columns.map((column) =>
                  column === 'client' ? (
                    <TD key={column} className="font-medium" {...frozenPropsOf(column)}>
                      {totalLabel}
                    </TD>
                  ) : (
                    figureCell(column, totals, true)
                  ),
                )}
              </TR>
            </TBody>
          </Table>
        </TableWrap>
      </div>

      {/* Phone */}
      <div className="space-y-4 p-3 md:hidden">
        {shownGroups.map((group) => (
          <section key={group.label} className="space-y-2">
            <GroupHeading label={group.label} count={group.total} />
            <PiCardList
              items={group.rows}
              getKey={(row) => keyOf.get(row) ?? row.name}
              render={(row) => {
                const key = keyOf.get(row) ?? row.name;
                const isOpen = expanded.has(key);
                return (
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-start gap-1.5">
                      <div className="min-w-0 flex-1">
                        <ClientCell row={row} />
                      </div>
                      <PiExpandButton
                        expanded={isOpen}
                        onClick={() => toggleExpanded(key)}
                        label={`${isOpen ? 'Hide' : 'Show'} details for ${row.name}`}
                      />
                    </div>
                    <CardFigures figures={figuresOf(row)} />
                    {isOpen && (
                      <DetailGrid row={row} compact className="mt-2 border-t border-hairline pt-2" />
                    )}
                  </div>
                );
              }}
            />
          </section>
        ))}
        <div className="rounded-md border border-hairline-strong bg-surface-sunken p-3">
          <div className="font-medium">{totalLabel}</div>
          <CardFigures figures={totals} />
        </div>
      </div>
      {showMore}
    </div>,
    visibleCount,
  );
}
